import { useCallback, useRef, useState } from 'react';
import {
  putSignedFile,
  requestUploadUrl,
  UploadAbortedError,
} from '../../agents/userFilesApi.ts';
import type { UploadedFile } from '../../agents/uploadNote.ts';
import { recordClientFailure } from '../../telemetry/rum.ts';

/**
 * Owns one console file upload at a time: mint a presigned URL, transfer the
 * bytes, report progress, and hand the finished path to the caller.
 *
 * A cancel is not a failure — it resolves to `idle` silently and reports
 * nothing, because nobody needs telling about a thing they just chose to stop.
 * A real failure both shows on the card and reaches RUM, since a browser-side
 * error nobody records is a failure nobody can find.
 */
export type UploadState =
  | { readonly status: 'idle' }
  | { readonly status: 'signing'; readonly name: string }
  | { readonly status: 'uploading'; readonly name: string; readonly progress: number }
  | { readonly status: 'error'; readonly name: string; readonly message: string };

export interface UseFileUploadOptions {
  /** Called once, with the stored file, after a successful transfer. */
  readonly onComplete: (file: UploadedFile) => void;
}

export interface UseFileUpload {
  readonly state: UploadState;
  /**
   * Begin an upload. Returns **false** when it was refused — one is already in
   * flight, or the file is empty — so the caller can avoid creating a
   * transcript card for an upload that will never happen. Silently dropping
   * the call would strand that card at "uploading" forever.
   */
  start: (file: File) => boolean;
  cancel: () => void;
}

export function useFileUpload({ onComplete }: UseFileUploadOptions): UseFileUpload {
  const [state, setState] = useState<UploadState>({ status: 'idle' });
  // A ref, not the state, guards re-entry: two clicks in one tick would both
  // read a stale `state` and start two uploads.
  const runningRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(
    (file: File): boolean => {
      if (runningRef.current) {
        return false;
      }
      // Caught here rather than at the server so the user gets an immediate
      // answer, and so an empty file never consumes a round trip.
      if (file.size === 0) {
        setState({ status: 'error', name: file.name, message: 'That file is empty.' });
        return false;
      }
      runningRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ status: 'signing', name: file.name });

      void (async () => {
        try {
          const ticket = await requestUploadUrl(file);
          setState({ status: 'uploading', name: file.name, progress: 0 });
          await putSignedFile({
            url: ticket.url,
            file,
            filename: ticket.path.split('/').pop() ?? file.name,
            contentType: file.type.length > 0 ? file.type : 'application/octet-stream',
            onProgress: (progress) =>
              setState({ status: 'uploading', name: file.name, progress }),
            signal: controller.signal,
          });
          setState({ status: 'idle' });
          onComplete({
            path: ticket.path,
            name: ticket.path.split('/').pop() ?? file.name,
            contentType: file.type,
          });
        } catch (err) {
          if (err instanceof UploadAbortedError) {
            setState({ status: 'idle' });
            return;
          }
          const message = err instanceof Error ? err.message : 'Could not upload that file.';
          setState({ status: 'error', name: file.name, message });
          // Reason code only — never the filename, which can identify a person.
          recordClientFailure(
            message.includes('storage') || message.includes('network')
              ? 'upload-transfer-failed'
              : 'upload-presign-failed',
          );
        } finally {
          runningRef.current = false;
          abortRef.current = null;
        }
      })();
      return true;
    },
    [onComplete],
  );

  const cancel = useCallback((): void => {
    abortRef.current?.abort();
  }, []);

  return { state, start, cancel };
}
